import { ChevronDown } from "lucide-react-native";
import { memo } from "react";

import type { KanbanColors } from "@/features/player-kanban/constants/colors";

import WatchlistItem from "@/features/player-kanban/components/molecules/WatchlistItem";
import type { Watchlist } from "@/features/player-kanban/components/molecules/WatchlistItem";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";

/**
 * Dropdown trigger surface style.
 * design-system: dynamic-style waiver — data-driven value (kanban theme palette)
 * @param colors - Resolved kanban theme colors
 */
const triggerStyle = (colors: KanbanColors) => ({
  backgroundColor: colors.cardBackground,
  borderColor: colors.border,
});

/**
 * Bottom safe-area inset padding (consumed by a gluestack ui Box).
 * design-system: dynamic-style waiver — data-driven value (safe-area inset)
 * @param bottomInset - Bottom safe-area inset in pixels
 */
const insetStyle = (bottomInset: number) => ({ paddingBottom: bottomInset });

/**
 * Props for WatchlistSectionView component.
 */
interface WatchlistSectionViewProps {
  /** Currently selected watchlist or null if none selected */
  readonly selectedWatchlist: Watchlist | null;
  /** Array of all available watchlists */
  readonly watchlists: readonly Watchlist[];
  /** Whether watchlists are currently being loaded */
  readonly isLoadingWatchlists?: boolean;
  /** Error message if watchlist loading failed */
  readonly watchlistError?: string | null;
  /** Kanban theme colors for consistent styling */
  readonly colors: KanbanColors;
  /** Callback when a watchlist is selected from the dropdown */
  readonly onValueChange: (watchlistId: string) => void;
  /** Whether the dropdown is currently open */
  readonly isOpen: boolean;
  /** Callback to open the dropdown */
  readonly onOpen: () => void;
  /** Callback to close the dropdown */
  readonly onClose: () => void;
  /** Bottom safe area inset for proper padding on notched devices */
  readonly bottomInset: number;
}

/**
 * WatchlistSectionView - View Component
 * Pure UI component for watchlist selection dropdown with loading/error states.
 * @remarks Uses bottomInset prop for safe area padding instead of SafeAreaView component.
 * @param root0 - The component props
 * @param root0.selectedWatchlist - Currently selected watchlist or null if none selected
 * @param root0.watchlists - Array of all available watchlists
 * @param root0.isLoadingWatchlists - Whether watchlists are currently being loaded
 * @param root0.watchlistError - Error message if watchlist loading failed
 * @param root0.onValueChange - Callback when a watchlist is selected from the dropdown
 * @param root0.colors - Kanban theme colors for consistent styling
 * @param root0.isOpen - Whether the dropdown is currently open
 * @param root0.onOpen - Callback to open the dropdown
 * @param root0.onClose - Callback to close the dropdown
 * @param root0.bottomInset - Bottom safe area inset for proper padding on notched devices
 */
const WatchlistSectionView = ({
  selectedWatchlist,
  watchlists,
  isLoadingWatchlists = false,
  watchlistError = null,
  colors,
  onValueChange,
  isOpen,
  onOpen,
  onClose,
  bottomInset,
}: WatchlistSectionViewProps) => (
  <Stack className="w-full">
    {/* Loading State */}
    {isLoadingWatchlists ? (
      <Stack className="min-h-11 w-full items-center justify-center rounded-sm border border-outline-subtle bg-surface-raised px-3 py-2">
        <Stack direction="horizontal" className="items-center gap-2">
          <Spinner size="small" />
          <Text variant="body" className="text-content-muted">
            Loading...
          </Text>
        </Stack>
      </Stack>
    ) : watchlistError ? (
      /* Error State */
      <Stack className="min-h-11 w-full items-center justify-center rounded-sm border border-status-error bg-surface-raised px-3 py-2">
        <Text variant="body" className="text-status-error">
          Failed to load
        </Text>
      </Stack>
    ) : !selectedWatchlist || watchlists.length === 0 ? (
      /* Empty State */
      <Stack className="min-h-11 w-full items-center justify-center rounded-sm border border-outline-subtle bg-surface-raised px-3 py-2">
        <Text variant="body" className="text-content-muted">
          No watchlists
        </Text>
      </Stack>
    ) : (
      /* Normal Inline Dropdown State */
      <Stack className="gap-2">
        <Pressable
          onPress={isOpen ? onClose : onOpen}
          className="min-h-11 w-full rounded-sm border px-3 py-2"
          // eslint-disable-next-line no-restricted-syntax -- data-driven kanban theme palette (colors.cardBackground/border); no closed color token
          UNSAFE_style={triggerStyle(colors)}
        >
          <Stack
            direction="horizontal"
            className="w-full flex-1 items-center justify-between"
          >
            <Text
              variant="title-sm-medium"
              className="flex-1 text-content-primary"
            >
              {selectedWatchlist.name}
            </Text>
            <Icon as={ChevronDown} size="md" className="text-content-muted" />
          </Stack>
        </Pressable>

        {isOpen && (
          <Box className="max-h-72 rounded-sm border border-outline-subtle bg-surface-raised">
            {/* design-system: dynamic-style waiver — data-driven value (safe-area inset) */}
            <Box
              className="web:flex-1 native:h-full native:w-full"
              // eslint-disable-next-line no-restricted-syntax -- measured safe-area bottom inset; no closed spacing token
              UNSAFE_style={insetStyle(bottomInset)}
            >
              <ScrollView bounces={false} contentContainerClassName="flex-grow">
                {watchlists.map(watchlist => (
                  <WatchlistItem
                    key={watchlist.id}
                    watchlist={watchlist}
                    isSelected={selectedWatchlist.id === watchlist.id}
                    onSelect={onValueChange}
                    onClose={onClose}
                  />
                ))}
              </ScrollView>
            </Box>
          </Box>
        )}
      </Stack>
    )}
  </Stack>
);

WatchlistSectionView.displayName = "WatchlistSectionView";

export default memo(WatchlistSectionView);
